// Library methods
import { useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { ThemeProvider, createTheme, useMediaQuery } from "@mui/material";

// MUI Components
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";

// Assets
import eyeLogo from "../../../../../assets/images/logos/eyelogo.png";

// Utilities
import {
  FixationLossMethodOptions,
  getTutorialCompletionText,
  getVisualField,
} from "../../../../../utils/examReportsHelper";
import { ValidatePDFContext } from "../../../../../contexts/ValidatePDFContext";
import { useLiveReportData } from "../../../../../hooks/useLiveReporData";
import { ReportEachSectionContext } from "../../../../../contexts/CommentContext";
import {
  ReportInfoHeaderContent,
  ReportInfoShared,
} from "../../../../../components/UI/ReportInfoShared";
import {
  DEFAULT_DECIBEL_VALUE_SCREENING,
  StimulusColors,
} from "../../../../../enums";
import useClinic from "../../../../../hooks/useClinic";
import { getLocalDateTimeWithZone } from "../../../../../utils/dateHelper";

const ReportInfo = () => {
  // internationalization
  const { t } = useTranslation();

  const { clinicTimezone } = useClinic();
  const { forBackendPdf } = useContext(ValidatePDFContext);
  const { examReport, eyeReport, duration } = useContext(
    ReportEachSectionContext
  );

  const isRLFast = useMemo(
    () => examReport?.exam?.algorithm === "SCREEN_FAST",
    [examReport?.exam?.algorithm]
  );

  const { falsePositivePercentage, falseNegative, fixationLossPercentage } =
    useLiveReportData(examReport);

  const theme = createTheme({
    typography: {
      allVariants: {
        fontFamily: ["Arial", "Verdana", "sans-serif"],
      },
    },
  });

  const isWiderThanSmallScreen = useMediaQuery(theme.breakpoints.up("sm"));

  const localDateAndTimeWithZone = useMemo(
    () =>
      examReport?.timing?.start
        ? getLocalDateTimeWithZone(examReport.timing.start, clinicTimezone)
        : null,
    [clinicTimezone, examReport?.timing?.start]
  );

  const fixationLossMethod = useMemo(
    () =>
      examReport?.stats?.fixationLossMethod ??
      FixationLossMethodOptions.EyeTracking,
    [examReport?.stats?.fixationLossMethod]
  );

  const gridSizeData = useMemo(
    () => [
      { sm: 4, md: 4, lg: 4, xl: 4 },
      { sm: 5, md: 5.5, lg: 5, xl: 5.5 },
      { sm: 3, md: 2.5, lg: 3, xl: 2.5 },
    ],
    []
  );

  // allows stimulus intensity to be displayed on screening exams
  // and hidden on thresholding exams
  const firstColumnData = useMemo(() => {
    let data = [
      {
        title: t("word_eye"),
        body: eyeReport === "Right" ? t("word_right") : t("word_left"),
      },
      {
        title: t("report_stimulus_size"),
        body: `${examReport?.exam?.stimulusSize}, ${t(examReport?.exam?.stimulusColor ?? StimulusColors.WHITE)}`,
      },
      {
        title: t("progress_analysis_exam_grid_type"),
        body: getVisualField(examReport?.exam?.grid),
      },
    ];

    // only display stimulus intensity on screening exams
    if (isRLFast) {
      data = [
        ...data,
        {
          title: t("report_stimulus_intensity"),
          body: `${examReport?.exam?.decibelValue ?? DEFAULT_DECIBEL_VALUE_SCREENING} dB`,
        },
      ];
    }

    return data;
  }, [examReport?.exam, eyeReport, isRLFast, t]);

  const infoData = useMemo(
    () => [
      [...firstColumnData],
      [
        { title: t("false_positive"), body: falsePositivePercentage },
        { title: t("false_negative"), body: falseNegative },
        { title: t("fixation_loss"), body: fixationLossPercentage },
        {
          title: t("fixation_loss_method"),
          body:
            fixationLossMethod === FixationLossMethodOptions.EyeTracking
              ? t("eye_tracking")
              : t("word_blind_spot"),
        },
      ],
      [
        {
          title: t("report_exam_date"),
          body: localDateAndTimeWithZone?.date ?? t("word_na"),
          isBlack: true,
        },
        {
          title: t("report_exam_time"),
          body: localDateAndTimeWithZone?.time ?? t("word_na"),
          isBlack: true,
        },
        { title: t("report_exam_duration"), body: duration },
        getTutorialCompletionText(
          examReport?.postProcessing?.tutorialCompletionState,
          t
        ) && {
          title: t("word_tutorial"),
          body: getTutorialCompletionText(
            examReport?.postProcessing?.tutorialCompletionState,
            t
          ),
          isBlack: true,
        },
      ],
    ],
    [
      firstColumnData,
      duration,
      falseNegative,
      falsePositivePercentage,
      fixationLossMethod,
      fixationLossPercentage,
      localDateAndTimeWithZone?.date,
      localDateAndTimeWithZone?.time,
      examReport?.postProcessing?.tutorialCompletionState,
      t,
    ]
  );

  return (
    <ThemeProvider theme={theme}>
      <Grid
        container
        columnSpacing={2}
        my={1}
        sx={{ display: "flex", alignItems: "start" }}
      >
        <Grid container item xs={12}>
          {forBackendPdf && (
            <Grid
              item
              xs={1}
              style={{
                alignItems: "center",
              }}
            >
              <img
                src={eyeLogo}
                alt="eye logo"
                style={{ width: "100%", height: "auto" }}
              />
            </Grid>
          )}
          <Grid container item xs={forBackendPdf ? 11 : 12} mb={1.5}>
            <Grid
              item
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              border={1}
              xs={12}
            >
              <Grid
                item
                sx={{
                  display: "flex",
                  background: "black",
                  width: 70,
                  height: "100%",
                  color: "white",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                xs={1.5}
                sm={1}
              >
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {eyeReport === "Right" ? "OD" : "OS"}
                </Typography>
              </Grid>
              <ReportInfoHeaderContent
                isWiderThanSmallScreen={isWiderThanSmallScreen}
                examTypeTranslation={`${t("report_visual_field")} - `}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          item
          display="flex"
          justifyContent={{ xs: "space-around", md: "space-between" }}
        >
          <ReportInfoShared data={infoData} gridSizeData={gridSizeData} />
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default ReportInfo;
